/**
 * simulationEngine.ts — Capacity Simulation Engine (pure functions)
 *
 * Rules:
 * - NO side effects
 * - NO Supabase calls
 * - NO DB persistence
 * - All calculations are deterministic and instantaneous
 */

// ─── Public API ───────────────────────────────────────────────────────────────

export const SIM_ROLES = ['Analista', 'Consultor', 'Gestor', 'Técnico'] as const;
export type SimRole = (typeof SIM_ROLES)[number];

export const SIM_DEFAULT_FTE: Record<SimRole, number> = {
  Analista: 2,
  Consultor: 1,
  Gestor: 1,
  Técnico: 1,
};

export const SIM_SHIFT_MINUTES = 480; // 8h default shift

export interface SimulationConfig {
  /** FTE per role. Values 0.5–5.0 */
  fteByRole: Record<string, number>;
  /** Multiplier on activity time. 0.5–1.5 (slider maps -50 to +50 → 1 + val/100) */
  timeFactor: number;
  /** Multiplier on demand volume. 0.5–2.0 (slider maps 50–200 → val/100) */
  demandFactor: number;
  /** Shift duration in minutes */
  shiftMinutes: number;
  /** SLA threshold in minutes for risk calculation */
  slaThresholdMinutes?: number;
}

export interface BaselineMetrics {
  efficiencyRate: number;        // 0–100
  slaCompliance: number;         // 0–100
  bottleneckCount: number;
  avgLeadTimeMinutes: number;
  totalActiveTasks: number;
}

export interface RoleUtilization {
  role: string;
  fte: number;
  capacityMinutes: number;        // FTE * shiftMinutes
  loadMinutes: number;            // simulated load
  utilization: number;            // loadMinutes / capacityMinutes
  level: 'ok' | 'attention' | 'bottleneck';
  queueMinutes: number;           // excess queue if overloaded
}

export interface SimulationResult {
  byRole: RoleUtilization[];
  bottleneckRoles: string[];
  simulatedLeadTime: number;       // minutes
  simulatedEfficiency: number;     // 0–100
  simulatedSlaCompliance: number;  // 0–100
  simulatedBottlenecks: number;
  /** Multiply baseline heatmap cell counts by this to get simulated load colors */
  heatmapLoadMultiplier: number;
  deltas: {
    /** % points change in efficiency (positive = improvement) */
    efficiency: number;
    /** % change in lead time (positive = worsened) */
    leadTime: number;
    /** % points change in SLA compliance (positive = improvement) */
    slaCompliance: number;
    /** Absolute change in bottleneck count */
    bottlenecks: number;
  };
}

// ─── Core calculation ─────────────────────────────────────────────────────────

function utilizationLevel(u: number): RoleUtilization['level'] {
  if (u <= 0.7) return 'ok';
  if (u <= 1.0) return 'attention';
  return 'bottleneck';
}

export function runSimulation(
  config: SimulationConfig,
  baseline: BaselineMetrics,
): SimulationResult {
  const { fteByRole, timeFactor, demandFactor, shiftMinutes } = config;
  const slaThreshold = config.slaThresholdMinutes ?? shiftMinutes;

  const roles = Object.keys(fteByRole).filter(r => fteByRole[r] > 0);
  const totalFte = roles.reduce((s, r) => s + fteByRole[r], 0);

  // Each task takes baseline activity time * timeFactor minutes
  // Distribute tasks evenly across roles (phase 1: no per-role task granularity)
  const simulatedTasks = baseline.totalActiveTasks * demandFactor;
  const avgTaskMinutes = (shiftMinutes / Math.max(1, baseline.totalActiveTasks)) * timeFactor;
  const totalLoadMinutes = simulatedTasks * avgTaskMinutes;
  const loadPerRole = roles.length > 0 ? totalLoadMinutes / roles.length : 0;

  const byRole: RoleUtilization[] = roles.map(role => {
    const fte = fteByRole[role];
    const capacityMinutes = fte * shiftMinutes;
    const loadMinutes = loadPerRole;
    const utilization = capacityMinutes > 0 ? loadMinutes / capacityMinutes : Infinity;
    const queueMinutes = utilization > 1 ? (utilization - 1) * shiftMinutes : 0;
    return {
      role,
      fte,
      capacityMinutes,
      loadMinutes,
      utilization,
      level: utilizationLevel(utilization),
      queueMinutes,
    };
  });

  const bottleneckRoles = byRole.filter(r => r.level === 'bottleneck').map(r => r.role);
  const maxQueueMinutes = byRole.reduce((m, r) => Math.max(m, r.queueMinutes), 0);

  // Simulated lead time = baseline * timeFactor + queue contribution
  const simulatedLeadTime = Math.max(
    1,
    Math.round(baseline.avgLeadTimeMinutes * timeFactor + maxQueueMinutes * 0.4),
  );

  // Efficiency degrades proportionally to overload
  const avgUtil = totalFte > 0
    ? byRole.reduce((s, r) => s + r.utilization, 0) / byRole.length
    : 1;
  const effFactor = avgUtil <= 1 ? Math.min(1, 1 + (1 - avgUtil) * 0.15) : 1 / avgUtil;
  const simulatedEfficiency = Math.min(100, Math.max(0, Math.round(baseline.efficiencyRate * effFactor)));

  // SLA compliance degrades if queue pushes beyond SLA threshold
  const slaRiskIncrement = maxQueueMinutes / Math.max(1, slaThreshold);
  const simulatedSlaCompliance = Math.min(
    100,
    Math.max(0, Math.round(baseline.slaCompliance * (1 - slaRiskIncrement * 0.5))),
  );

  const simulatedBottlenecks = bottleneckRoles.length;

  const deltas = {
    efficiency: simulatedEfficiency - baseline.efficiencyRate,
    leadTime:
      baseline.avgLeadTimeMinutes > 0
        ? Math.round(((simulatedLeadTime - baseline.avgLeadTimeMinutes) / baseline.avgLeadTimeMinutes) * 100)
        : 0,
    slaCompliance: simulatedSlaCompliance - baseline.slaCompliance,
    bottlenecks: simulatedBottlenecks - baseline.bottleneckCount,
  };

  return {
    byRole,
    bottleneckRoles,
    simulatedLeadTime,
    simulatedEfficiency,
    simulatedSlaCompliance,
    simulatedBottlenecks,
    heatmapLoadMultiplier: demandFactor,
    deltas,
  };
}

/** Build a default SimulationConfig from the standard role list */
export function defaultSimulationConfig(): SimulationConfig {
  return {
    fteByRole: { ...SIM_DEFAULT_FTE },
    timeFactor: 1,
    demandFactor: 1,
    shiftMinutes: SIM_SHIFT_MINUTES,
  };
}
